/**
 * Indicador visual do estado da conexão /ws/live.
 *
 * - `open`         → bolinha verde pulsante + "ao vivo"
 * - `connecting`   → bolinha âmbar sem pulso + "conectando"
 * - `reconnecting` → bolinha âmbar pulsante + "reconectando"
 * - `closed`       → bolinha cinza + "offline"
 * - `verifying`    → bolinha âmbar escura pulsante + "verificando sessão"
 * - `unauthorized` → bolinha vermelha + "sessão encerrada" (terminal)
 *
 * Reaproveita as classes `nx-live-badge*` e `nx-live-dot` existentes;
 * quando o estado é âmbar ou cinza, um `style` inline sobrescreve a
 * cor para usar tokens de warning/neutral em vez de success.
 *
 * `unauthorized` é o único estado que separa a cor do PONTO da cor do
 * TEXTO, e a razão está medida no navegador (11,25px, os dois temas):
 *
 * | elemento | cor              | claro    | escuro   | mínimo |
 * |----------|------------------|----------|----------|---------|
 * | ponto    | `--nx-danger-500`|  4,83:1  |  3,95:1  | 3:1 (1.4.11) |
 * | texto    | `--nx-ink`       | 15,96:1  | 16,76:1  | 4,5:1 (1.4.3) |
 *
 * `verifying` segue a mesma regra, com `--nx-warning-600` no ponto — ver
 * a nota em `color()`.
 *
 * `--nx-danger-500` como TEXTO mediria 3,95:1 no tema escuro e reprovaria
 * o 1.4.3 — por isso o vermelho fica só no ponto, que é elemento
 * não-textual. Não mexi na cor dos outros estados: o âmbar de
 * "conectando"/"reconectando" tem problema equivalente no tema CLARO
 * (2,2:1) e é anterior a esta entrega.
 */
import { useLiveConnection, type LiveConnStatus } from '../lib/useLiveEvents.js';

function label(s: LiveConnStatus): string {
  switch (s) {
    case 'open': return 'ao vivo';
    case 'connecting': return 'conectando';
    case 'reconnecting': return 'reconectando';
    case 'closed': return 'offline';
    // "encerrada", não "expirada": dos quatro gatilhos do 1008, expirar é o
    // menos frequente, e no mais comum (admin muda o perfil) "expirada"
    // seria duplamente falso. "Encerrada" é causalmente neutra.
    case 'verifying': return 'verificando sessão';
    case 'unauthorized': return 'sessão encerrada';
  }
}

/** Texto do `title`. Só o estado terminal diz o que fazer a respeito. */
function descricao(s: LiveConnStatus): string {
  return s === 'unauthorized'
    ? 'Conexão em tempo real: sessão encerrada — entre novamente para voltar a receber dados ao vivo'
    : `Conexão em tempo real: ${label(s)}`;
}

/** Cor do ponto. Elemento não-textual: o mínimo é 3:1 (WCAG 1.4.11). */
function color(s: LiveConnStatus): string {
  switch (s) {
    case 'open': return 'var(--nx-success-500)';
    case 'connecting':
    case 'reconnecting': return 'var(--nx-warning-500)';
    // Mesma família cromática de "reconectando" — é o mesmo assunto, a
    // conexão sendo restabelecida — mas um tom abaixo, e não por gosto: o
    // `--nx-warning-500` mede 2,15:1 sobre o fundo claro e reprovaria o
    // 1.4.11 (mínimo 3:1 para elemento não-textual). O `600` passa nos
    // dois temas (5,02:1 claro / 3,80:1 escuro). Não corrigi o `500` de
    // "conectando"/"reconectando": a falha é anterior a esta entrega e
    // trocar cor de estado existente é decisão de design, não minha — mas
    // não vou criar a quarta instância dela.
    case 'verifying': return 'var(--nx-warning-600)';
    case 'closed': return 'var(--nx-neutral-400)';
    case 'unauthorized': return 'var(--nx-danger-500)';
  }
}

/** Cor do texto do badge. Ver o bloco do topo sobre 1.4.3. */
function corDoTexto(s: LiveConnStatus): string {
  return s === 'unauthorized' || s === 'verifying' ? 'var(--nx-ink)' : color(s);
}

export function LiveIndicator({ compact = false }: { compact?: boolean }) {
  const { status } = useLiveConnection();
  const on = status === 'open';
  const cls = on ? 'nx-live-badge nx-live-badge--on' : 'nx-live-badge';
  const inlineStyle = on
    ? undefined
    : {
        color: corDoTexto(status),
        borderColor: 'var(--nx-line-strong)',
        background: 'var(--nx-bg)',
      };
  return (
    <span className={cls} style={inlineStyle} aria-live="polite" title={descricao(status)}>
      <span
        className="nx-live-dot"
        style={
          !on
            ? {
                background: color(status),
                animation:
                  status === 'reconnecting' || status === 'verifying'
                    ? 'nx-ripple 1.4s ease-out infinite'
                    : undefined,
              }
            : undefined
        }
      />
      {compact ? null : label(status)}
    </span>
  );
}
